import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";
import CTABand from "@/components/CTABand";
import { Reveal, SectionHead, ease } from "@/components/Reveal";
import { COMPANY, PILLARS, WHY_POINTS, WHY_INTRO, WHY_COMMITMENT, IMAGES } from "@/data/content";

const PILLAR_IMAGES = [IMAGES.mobile, IMAGES.server, IMAGES.abstract];

export default function About() {
  return (
    <main>
      {/* Hero */}
      <section className="noise relative overflow-hidden bg-midnight" data-testid="about-hero">
        <NetworkCanvas className="absolute inset-0 h-full w-full opacity-40" density={30000} interactive={false} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-midnight/60 via-transparent to-midnight" />
        <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-6 pb-24 pt-40 lg:grid-cols-2 lg:px-8 lg:pb-32 lg:pt-48">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease }}
              className="flex items-center gap-4"
            >
              <span className="h-px w-12 bg-gradient-to-r from-iris to-aqua" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-aqua">About Protech</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease }}
              className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl"
              data-testid="about-hero-title"
            >
              Building a More <span className="text-gradient">Secure Digital Future</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              data-testid="about-hero-tagline"
            >
              {COMPANY.tagline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.38, ease }}
              className="mt-10 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-6 py-4 backdrop-blur-md"
              data-testid="about-hero-incorporated"
            >
              <span className="font-display text-3xl font-extrabold text-gradient">2016</span>
              <span className="text-sm leading-snug text-white/60">
                Protech Future System Sdn Bhd
                <br />
                was incorporated in 2016.
              </span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.45)]">
              <img
                src={IMAGES.dots}
                alt="Abstract network of glowing connected nodes"
                className="h-[320px] w-full object-cover sm:h-[440px]"
                data-testid="about-hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission / Vision / Value */}
      <section className="bg-white" data-testid="about-pillars-section">
        <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <SectionHead num="01" eyebrow="What Drives Us" title="Mission, Vision and Value." />
          <div className="mt-16 space-y-0">
            {PILLARS.map((pillar, i) => (
              <Reveal key={pillar.num} delay={0.05}>
                <div
                  className={`grid items-center gap-10 border-t border-hairline py-14 lg:grid-cols-12 lg:gap-16 ${
                    i === PILLARS.length - 1 ? "border-b" : ""
                  }`}
                  data-testid={`about-pillar-${pillar.num}`}
                >
                  <div className="lg:col-span-2">
                    <span className="font-display text-5xl font-extrabold text-hairline lg:text-6xl">{pillar.num}</span>
                  </div>
                  <div className="lg:col-span-6">
                    <h3 className="font-display text-2xl font-bold tracking-tight text-navy lg:text-3xl">
                      {pillar.title}
                    </h3>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-steel lg:text-lg">{pillar.text}</p>
                  </div>
                  <div className="hidden lg:col-span-4 lg:block">
                    <div className="overflow-hidden rounded-2xl border border-hairline shadow-[0_20px_50px_rgba(17,20,43,0.12)]">
                      <img
                        src={PILLAR_IMAGES[i]}
                        alt={`${pillar.title} — abstract technology visual`}
                        className="h-52 w-full object-cover transition-transform duration-500 hover:scale-105"
                        loading="lazy"
                        data-testid={`about-pillar-image-${pillar.num}`}
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="noise relative overflow-hidden bg-gradient-to-b from-midnight to-navy-light" data-testid="about-why-section">
        <div className="pointer-events-none absolute -left-24 top-1/3 h-80 w-80 rounded-full bg-iris/15 blur-[140px]" />
        <div className="relative mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
          <SectionHead
            num="02"
            eyebrow="Why Choose Us"
            title={WHY_INTRO}
            dark
          />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-why-grid">
            {WHY_POINTS.map((point, i) => (
              <Reveal key={point.num} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`about-why-point-${point.num}`}
                  className="group h-full rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md transition-[transform,border-color] duration-300 hover:-translate-y-2 hover:border-aqua/60"
                >
                  <span className="font-display text-sm font-extrabold text-aqua">/{point.num}</span>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-white">{point.title}</h3>
                  <span className="mt-8 block h-0.5 w-8 bg-gradient-to-r from-iris to-aqua transition-all duration-300 group-hover:w-full" />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-start justify-between gap-8 rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md lg:flex-row lg:items-center">
              <p className="max-w-2xl font-display text-xl font-semibold leading-relaxed text-white lg:text-2xl" data-testid="about-why-commitment">
                “{WHY_COMMITMENT}”
              </p>
              <a
                href="/contact-us"
                data-testid="about-why-cta"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-iris px-8 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-aqua hover:text-midnight"
              >
                Talk to Us
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
